import type { AgentQuery } from '@monorepo/shared/schemas/agents'
import { ENTITY_STATUS } from '@monorepo/shared/schemas/common'
import { Bot, Download, Plus, RefreshCw, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useEnvironment } from '@/features/environments'
import { PageTitle } from '@/shared/components/common/PageTitle'
import { ViewToggle } from '@/shared/components/common/tabs/ViewToggle'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Card, CardContent, CardHeader } from '@/shared/components/ui/card'
import { ConfirmationDialog } from '@/shared/components/ui/confirmation-dialog'
import { Input } from '@/shared/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import {
  useAgentsList,
  useDeleteAgent,
  useUpdateAgent,
} from '../hooks/useAgents'
import type { Agent } from '../types/agent.types'
import { AgentDrawer } from './AgentDrawer'
import { AgentsGrid } from './AgentsGrid'
import { AgentsTable } from './AgentsTable'

interface AgentsPageProps {
  onViewAgent?: (agent: Agent) => void
}

export function AgentsPage({ onViewAgent }: AgentsPageProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid')
  const [deleteAgent, setDeleteAgent] = useState<Agent | null>(null)
  const [editingAgent, setEditingAgent] = useState<Agent | undefined>(undefined)
  const [showAgentFormDrawer, setShowAgentFormDrawer] = useState(false)

  // Create filters object for API
  const filters = useMemo((): AgentQuery => {
    const filterObj: AgentQuery = {}

    if (searchQuery.trim()) {
      filterObj.search = searchQuery.trim()
    }

    if (statusFilter !== 'all') {
      filterObj.status = statusFilter as AgentQuery['status']
    }

    return filterObj
  }, [searchQuery, statusFilter])

  // Gate on environment — backend requires X-Environment-ID for /agents
  const { currentEnvironment } = useEnvironment()

  // Fetch agents data from API using convenience hook
  const { agents, isLoading, error, refetch } = useAgentsList(filters, {
    enabled: !!currentEnvironment,
  })
  const deleteAgentMutation = useDeleteAgent()
  const updateAgentMutation = useUpdateAgent()

  // Calculate stats from real data
  const activeAgents = agents.filter(
    a => a.status === ENTITY_STATUS.active
  ).length
  const draftAgents = agents.filter(
    a => a.status === ENTITY_STATUS.draft
  ).length
  const totalActions = agents.reduce(
    (sum, agent) => sum + (agent.totalSignalsCount || 0),
    0
  )

  const handleCreateAgent = () => {
    setEditingAgent(undefined)
    setShowAgentFormDrawer(true)
  }

  const handleEditAgent = (agent: Agent) => {
    setEditingAgent(agent)
    setShowAgentFormDrawer(true)
  }

  const handleAgentAction = async (action: string, agent: Agent) => {
    switch (action) {
      case 'edit':
        handleEditAgent(agent)
        break
      case 'activate':
        // Handle activate (draft -> active)
        try {
          await updateAgentMutation.mutateAsync({
            id: agent.id,
            data: { status: ENTITY_STATUS.active },
          })
        } catch (error) {
          toast.error(extractErrorMessage(error))
        }
        break
      case 'deactivate':
        // Handle deactivate (active -> draft)
        try {
          await updateAgentMutation.mutateAsync({
            id: agent.id,
            data: { status: ENTITY_STATUS.draft },
          })
        } catch (error) {
          toast.error(extractErrorMessage(error))
        }
        break
      case 'delete':
        setDeleteAgent(agent)
        break
    }
  }

  const handleConfirmDelete = async () => {
    if (deleteAgent) {
      try {
        await deleteAgentMutation.mutateAsync(deleteAgent.id)
        setDeleteAgent(null)
      } catch (error) {
        toast.error(extractErrorMessage(error))
      }
    }
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex flex-col gap-4 md:flex-row md:justify-between md:items-start'>
        <div>
          <PageTitle
            title='Agents'
            subtitle='Manage your AI agents and configure their signal tracking'
          />
          {/* Stats Chips */}
          <div className='flex flex-wrap gap-2 mt-3'>
            <Badge variant='secondary' className='text-sm'>
              {agents.length} Total
            </Badge>
            <Badge variant='default' className='text-sm'>
              {activeAgents} Active
            </Badge>
            <Badge variant='secondary' className='text-sm'>
              {draftAgents} Draft
            </Badge>
            <Badge variant='outline' className='text-sm'>
              {totalActions} Actions
            </Badge>
          </div>
        </div>
        <div className='flex gap-3 items-center w-full md:w-auto'>
          <Button
            onClick={handleCreateAgent}
            size='lg'
            className='flex-1 md:flex-initial'
          >
            <Plus className='w-5 h-5 mr-2' />
            Create Agent
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className='flex flex-col gap-4 md:flex-row md:items-center md:justify-between'>
        <div className='flex gap-2 items-center flex-1 max-w-2xl'>
          <div className='relative flex-1'>
            <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4' />
            <Input
              placeholder='Search agents...'
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className='pl-10'
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className='w-1/4'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All Status</SelectItem>
              <SelectItem value={ENTITY_STATUS.active}>Active</SelectItem>
              <SelectItem value={ENTITY_STATUS.draft}>Draft</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className='flex gap-2 items-center'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw
              className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`}
            />
          </Button>

          <Button variant='outline' size='sm'>
            <Download className='w-4 h-4' />
          </Button>

          <ViewToggle currentView={viewMode} onViewChange={setViewMode} />
        </div>
      </div>

      {/* Agents Content */}
      {isLoading ? (
        viewMode === 'table' ? (
          <div className='bg-transparent border rounded-lg'>
            <div className='animate-pulse space-y-4 p-6'>
              <div className='grid grid-cols-6 gap-4 pb-4 border-b'>
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                    key={i}
                    className='h-4 bg-slate-300 dark:bg-slate-600 rounded'
                  ></div>
                ))}
              </div>
              {Array.from({ length: 8 }).map((_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                <div key={i} className='grid grid-cols-6 gap-4 py-3'>
                  {Array.from({ length: 6 }).map((_, j) => (
                    <div
                      // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                      key={j}
                      className='h-4 bg-slate-300 dark:bg-slate-600 rounded'
                    ></div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
            {Array.from({ length: 9 }).map((_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
              <Card key={i} className='animate-pulse'>
                <CardHeader className='pb-4'>
                  <div className='flex justify-between items-start mb-2'>
                    <div className='h-5 bg-slate-300 dark:bg-slate-600 rounded w-16'></div>
                    <div className='h-8 w-8 bg-slate-300 dark:bg-slate-600 rounded'></div>
                  </div>
                  <div className='h-6 bg-slate-300 dark:bg-slate-600 rounded w-3/4 mb-2'></div>
                  <div className='h-4 bg-slate-300 dark:bg-slate-600 rounded w-1/2'></div>
                </CardHeader>

                <CardContent className='space-y-4'>
                  <div className='h-4 bg-slate-300 dark:bg-slate-600 rounded w-full'></div>
                  <div className='h-4 bg-slate-300 dark:bg-slate-600 rounded w-4/5'></div>

                  <div className='grid grid-cols-3 gap-4 py-4 border-t'>
                    {Array.from({ length: 3 }).map((_, j) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                      <div key={j} className='text-center space-y-2'>
                        <div className='h-6 bg-slate-300 dark:bg-slate-600 rounded mx-auto w-8'></div>
                        <div className='h-3 bg-slate-300 dark:bg-slate-600 rounded w-full'></div>
                      </div>
                    ))}
                  </div>

                  <div className='flex flex-wrap gap-2 border-t pt-3'>
                    {Array.from({ length: 3 }).map((_, j) => (
                      <div
                        // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                        key={j}
                        className='h-5 bg-slate-300 dark:bg-slate-600 rounded w-16'
                      ></div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )
      ) : error ? (
        <Card>
          <CardContent className='pt-16 pb-16'>
            <div className='text-center'>
              <Bot className='w-16 h-16 mx-auto text-muted-foreground/50 mb-4' />
              <h3 className='text-lg font-medium mb-2'>Error loading agents</h3>
              <p className='text-muted-foreground mb-6'>
                There was an error loading your agents. Please try again.
              </p>
              <Button onClick={() => refetch()}>
                <RefreshCw className='w-4 h-4 mr-2' />
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : viewMode === 'table' ? (
        <AgentsTable
          agents={agents}
          onEditAgent={handleEditAgent}
          onViewAgent={onViewAgent}
        />
      ) : (
        <AgentsGrid
          agents={agents}
          onViewAgent={onViewAgent}
          onAction={handleAgentAction}
          onCreateAgent={handleCreateAgent}
          searchQuery={searchQuery}
          statusFilter={statusFilter}
        />
      )}

      <ConfirmationDialog
        open={!!deleteAgent}
        onOpenChange={open => !open && setDeleteAgent(null)}
        title='Delete Agent'
        description={`Are you sure you want to delete "${deleteAgent?.name}"? This action cannot be undone and will permanently remove the agent and all its associated data.`}
        confirmText='Delete'
        cancelText='Cancel'
        variant='destructive'
        isLoading={deleteAgentMutation.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteAgent(null)}
      />

      {/* Agent Form Drawer */}
      <AgentDrawer
        agent={editingAgent}
        open={showAgentFormDrawer}
        onOpenChange={setShowAgentFormDrawer}
      />
    </div>
  )
}
